import React from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Image,
  Button,
  Stack,
  SimpleGrid,
  List,
  ListItem,
  Icon,
  Badge
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import assets from './assets';
import {
  platformCards,
  platformGuarantees,
  includedDashboards,
  howItWorksSteps
} from './marketingContent';
import { fallbackTiers } from './Subscribe/tiersConfig';
import MarketingFooter from './MarketingFooter';
import { marketingHeadingStyles } from './marketingStyles';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const TeliviIT = ({ children }) => {
  return (
    <Box fontFamily="var(--telivi-font-body)" sx={marketingHeadingStyles}>
      {children || (
        <>
          <Box bgGradient="linear(to-b, gray.50, white)" pt={{ base: 16, lg: 24 }} pb={{ base: 12, lg: 16 }}>
            <Container maxW="container.xl">
              <Flex
                direction={{ base: 'column', lg: 'row' }}
                align={{ base: 'center', lg: 'flex-start' }}
                gap={12}
              >
                <Stack flex={1} spacing={6}>
                  <Flex
                    align="center"
                    gap={3}
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.100"
                    px={4}
                    py={2}
                    borderRadius="full"
                    w="fit-content"
                  >
                    <Icon as={FaShieldAlt} color="green.500" />
                    <Text fontSize="sm" fontWeight="600" color="green.700">
                      True tenant isolation with dedicated infra, databases, and domains.
                    </Text>
                  </Flex>

                  <Heading
                    as="h1"
                    fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                    lineHeight="1.05"
                    fontFamily="var(--telivi-font-heading)"
                  >
                    Your Business. Your Platform. Never Shared.
                  </Heading>
                  <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600">
                    Isolated platforms for Property, Case, and Contract management with Helpdesk,
                    Publishing, and Payments dashboards built in.
                  </Text>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                    <Button
                      as={RouterLink}
                      to="/pricing"
                      size="lg"
                      colorScheme="blue"
                    >
                      View Pricing
                    </Button>
                    <Button
                      as={RouterLink}
                      to="/platforms"
                      size="lg"
                      variant="outline"
                      colorScheme="blue"
                    >
                      Explore Platforms
                    </Button>
                  </Stack>
                </Stack>

                <Box flex={1} w="full">
                  <Box
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="xl"
                    p={{ base: 3, md: 4 }}
                    overflow="hidden"
                  >
                    <Image
                      src={assets.pp2}
                      alt="Telivi dashboard preview"
                      w="100%"
                      h={{ base: 'auto', lg: '360px' }}
                      objectFit="cover"
                      objectPosition="center"
                      borderRadius="xl"
                    />
                  </Box>
                </Box>
              </Flex>

              <Text
                color="blue.500"
                fontWeight="600"
                textTransform="uppercase"
                mt={{ base: 10, md: 12 }}
              >
                Platform Guarantees
              </Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mt={4}>
                {platformGuarantees.map((guarantee) => (
                  <Flex
                    key={guarantee.title}
                    align="center"
                    gap={3}
                    bg="white"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    px={4}
                    py={3}
                    boxShadow="sm"
                  >
                    <Icon as={guarantee.icon} color="blue.500" boxSize={5} />
                    <Text fontWeight="600" fontSize="sm">
                      {guarantee.title}
                    </Text>
                  </Flex>
                ))}
              </SimpleGrid>
              <Text fontSize="sm" color="gray.500" mt={4}>
                Every Telivi customer operates on fully isolated infrastructure. Applications,
                databases, and deployments are never shared between tenants.
              </Text>
            </Container>
          </Box>

          <Box py={{ base: 16, md: 20 }}>
            <Container maxW="container.xl">
              <Stack spacing={4} textAlign="center" mb={12}>
                <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                  Choose Your Core Platform
                </Text>
                <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
                  Launch the platform built for your operations
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  Each platform ships with isolated infrastructure and the dashboards your teams
                  need from day one.
                </Text>
              </Stack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                {platformCards.map((platform) => (
                  <Box
                    key={platform.title}
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.100"
                    p={8}
                  >
                    <Stack spacing={4}>
                      <Flex align="center" gap={3}>
                        <Flex
                          align="center"
                          justify="center"
                          bg="blue.50"
                          color="blue.600"
                          borderRadius="lg"
                          w={12}
                          h={12}
                        >
                          <Icon as={platform.icon} boxSize={6} />
                        </Flex>
                        <Heading as="h3" fontSize="xl">
                          {platform.title}
                        </Heading>
                      </Flex>
                      <Text color="gray.600">{platform.description}</Text>
                      <Box>
                        <Badge colorScheme="blue" mb={2}>
                          Core Service Dashboard
                        </Badge>
                        <Text fontWeight="600">{platform.coreService}</Text>
                      </Box>
                      <Box>
                        <Badge colorScheme="gray" mb={2}>
                          Included Dashboards
                        </Badge>
                        <List spacing={2} color="gray.600">
                          {includedDashboards
                            .filter((dashboard) => dashboard.title !== 'Core Service Dashboard')
                            .map((dashboard) => (
                              <ListItem key={dashboard.title}>
                                <Flex align="center" gap={2}>
                                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                                  <Text>{dashboard.title}</Text>
                                </Flex>
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                      <Button as={RouterLink} to="/platforms" variant="outline" colorScheme="blue">
                        Explore
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </SimpleGrid>
            </Container>
          </Box>

          <Box py={{ base: 16, md: 20 }} bg="gray.50">
            <Container maxW="container.xl">
              <Stack spacing={4} textAlign="center" mb={12}>
                <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                  Included Dashboards
                </Text>
                <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
                  Everything you need to run operations in one place
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  Every platform includes the same suite of operational dashboards.
                </Text>
              </Stack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {includedDashboards.map((dashboard) => (
                  <Box
                    key={dashboard.title}
                    bg="white"
                    borderRadius="2xl"
                    p={6}
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.100"
                  >
                    <Flex align="center" gap={3} mb={3}>
                      <Flex
                        align="center"
                        justify="center"
                        bg="orange.50"
                        color="orange.500"
                        borderRadius="lg"
                        w={10}
                        h={10}
                      >
                        <Icon as={dashboard.icon} />
                      </Flex>
                      <Heading as="h3" fontSize="lg">
                        {dashboard.title}
                      </Heading>
                    </Flex>
                    <Text color="gray.600">{dashboard.description}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Container>
          </Box>

          <Box py={{ base: 16, md: 20 }}>
            <Container maxW="container.xl">
              <Stack spacing={4} textAlign="center" mb={12}>
                <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                  How Telivi Works
                </Text>
                <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
                  Clear onboarding from pricing to dashboard
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  Pricing → Subscribe → Onboarding → Dashboard. Setup turnaround is 5-18 business
                  days depending on tier.
                </Text>
              </Stack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {howItWorksSteps.map((step, index) => (
                  <Box
                    key={step.title}
                    bg="white"
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="sm"
                  >
                    <Badge colorScheme="blue" mb={3}>
                      Step {index + 1}
                    </Badge>
                    <Heading as="h3" fontSize="lg" mb={2}>
                      {step.title}
                    </Heading>
                    <Text color="gray.600">{step.description}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Container>
          </Box>

          <Box py={{ base: 16, md: 20 }} bg="gray.50">
            <Container maxW="container.xl">
              <Stack spacing={4} textAlign="center" mb={12}>
                <Text color="blue.500" fontWeight="600" textTransform="uppercase">
                  Pricing Preview
                </Text>
                <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
                  Choose a tier, then subscribe and launch
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  All tiers include isolated infrastructure, with setup and turnaround based on the
                  level of operational coverage you need.
                </Text>
              </Stack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {fallbackTiers.map((tier) => {
                  const monthlyLabel = tier.monthlyPrice
                    ? `${formatCurrency(tier.monthlyPrice)}/mo`
                    : 'Starting at custom monthly rate';

                  return (
                    <Box
                      key={tier.id}
                      bg="white"
                      color="gray.900"
                      borderRadius="2xl"
                      p={6}
                      border={tier.recommended ? '2px solid' : '1px solid'}
                      borderColor={tier.recommended ? 'blue.400' : 'gray.100'}
                      boxShadow="lg"
                    >
                      <Stack spacing={4}>
                        <Box>
                          <Text fontWeight="600" color="blue.500">
                            Tier {tier.tier}
                          </Text>
                          <Heading as="h3" fontSize="2xl">
                            {tier.label}
                          </Heading>
                        </Box>
                        <Text fontSize="lg">{monthlyLabel}</Text>
                        <Text fontSize="sm" color="gray.600">
                          Setup fee: {formatCurrency(tier.setupFee)} · Turnaround {tier.turnaroundDays}
                          days
                        </Text>
                        <Button
                          as={RouterLink}
                          to="/pricing"
                          colorScheme="blue"
                          variant={tier.recommended ? 'solid' : 'outline'}
                        >
                          View full pricing
                        </Button>
                      </Stack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Container>
          </Box>

          <Box py={{ base: 16, md: 20 }}>
            <Container maxW="container.xl">
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align="center"
                justify="space-between"
                gap={8}
                bgGradient="linear(to-r, blue.50, orange.50)"
                borderRadius="2xl"
                p={{ base: 8, md: 12 }}
              >
                <Box>
                  <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} mb={3}>
                    Launch your isolated platform in days, not quarters.
                  </Heading>
                  <Text color="gray.700" fontSize="lg">
                    Subscribe, onboard, and access your dedicated Telivi environment with clear
                    turnaround times.
                  </Text>
                </Box>
                <Button as={RouterLink} to="/pricing" size="lg" colorScheme="blue">
                  Get started
                </Button>
              </Flex>
            </Container>
          </Box>

          <MarketingFooter />
        </>
      )}
    </Box>
  );
};

export default TeliviIT;
